import { useEffect, useRef } from 'react';
import type { VideoType } from '@sola/utils/video.ts';
export type IframePlayerRef = {
  iframe: HTMLIFrameElement;
  provider: 'vimeo' | 'youtube';
};
const vimeoMatch = /vimeo\.com\/(video\/|event\/)?(\d+)(?:\/([\w-]+))?/;
const youtubeMatch = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))([\w-]{11})/;
export const buildVimeoEmbedUrl = (src: string, isBackground: boolean, autoplay = false): string | undefined => {
  const url = new URL(src, 'https://vimeo.com');
  const matches = src.match(vimeoMatch);
  const videoId = matches?.[2];
  if (!videoId) {
    return undefined;
  }
  const hParam = url.searchParams.get('h') || matches?.[3] || '';
  const params: Record<string, string | number> = {
    dnt: 1,
    transparent: 0,
    playsinline: 1,
    ...(hParam ? {
      h: hParam
    } : {}),
    ...(isBackground ? {
      background: 1,
      autoplay: 1,
      loop: 1,
      muted: 1
    } : {}),
    ...(!isBackground && autoplay ? {
      autoplay: 1
    } : {})
  };
  const query = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString();
  return `https://player.vimeo.com/video/${videoId}?${query}`;
};
export const buildYouTubeEmbedUrl = (src: string, isBackground: boolean, autoplay = false): string | undefined => {
  const matches = src.match(youtubeMatch);
  const videoId = matches?.[1];
  if (!videoId) {
    return undefined;
  }
  const isNoCookie = src.includes('youtube-nocookie.com');
  const embedBase = isNoCookie ? 'https://www.youtube-nocookie.com/embed' : 'https://www.youtube.com/embed';
  const params: Record<string, string | number> = {
    enablejsapi: 1,
    playsinline: 1,
    rel: 0,
    modestbranding: 1,
    ...(isBackground ? {
      autoplay: 1,
      mute: 1,
      loop: 1,
      playlist: videoId,
      controls: 0
    } : {}),
    ...(!isBackground && autoplay ? {
      autoplay: 1
    } : {})
  };
  const query = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString();
  return `${embedBase}/${videoId}?${query}`;
};

/** Sends play/pause to a provider iframe via postMessage. */
export const postIframeCommand = (ref: IframePlayerRef | null, command: 'play' | 'pause') => {
  if (!ref?.iframe.contentWindow) {
    return;
  }
  if (ref.provider === 'vimeo') {
    ref.iframe.contentWindow.postMessage(JSON.stringify({
      method: command
    }), 'https://player.vimeo.com');
  } else {
    const func = command === 'play' ? 'playVideo' : 'pauseVideo';
    const origin = new URL(ref.iframe.src).origin;
    ref.iframe.contentWindow.postMessage(JSON.stringify({
      event: 'command',
      func
    }), origin);
  }
};
export const getPlayerWidth = (isBackground: boolean, isPortrait: boolean) => {
  if (isBackground) {
    return 'inherit';
  }
  if (isPortrait) {
    return 'auto';
  }
  return '100%';
};

/** Creates a provider iframe and wires postMessage for play/pause control. */
export const useVideoIframe = ({
  src,
  videoType,
  isBackground,
  title,
  autoplay,
  enabled = true,
  onPlay,
  onLoadStart,
  onReady
}: {
  src: string;
  videoType: VideoType;
  isBackground: boolean;
  title: string;
  autoplay?: boolean;
  /**
   * Gates iframe creation. Inline provider videos pass `false` until the visitor
   * activates the facade, which keeps the provider's player bundle (~1 MB for
   * YouTube) off the initial load for embeds nobody plays.
   */
  enabled?: boolean;
  onPlay?: () => void;
  onLoadStart?: () => void;
  onReady?: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<IframePlayerRef | null>(null);
  const onPlayRef = useRef(onPlay);
  onPlayRef.current = onPlay;
  const onLoadStartRef = useRef(onLoadStart);
  onLoadStartRef.current = onLoadStart;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const autoplayRef = useRef(autoplay);
  autoplayRef.current = autoplay;
  useEffect(() => {
    onLoadStartRef.current?.();
    if (!enabled) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    if (videoType !== 'vimeo' && videoType !== 'youtube') {
      return;
    }
    const embedUrl = videoType === 'vimeo' ? buildVimeoEmbedUrl(src, isBackground, autoplayRef.current) : buildYouTubeEmbedUrl(src, isBackground, autoplayRef.current);
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl || src; // Fallback to raw URL if ID extraction fails
    iframe.title = title;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen');
    iframe.style.display = 'block';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    playerRef.current = {
      iframe,
      provider: videoType
    };

    // Listen for play events via postMessage
    const handleMessage = (event: MessageEvent) => {
      const parseData = () => {
        try {
          return typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch {
          return null;
        }
      };
      if (videoType === 'vimeo' && event.origin === 'https://player.vimeo.com') {
        const data = parseData();
        if (data?.event === 'play' || data?.method === 'play') {
          onPlayRef.current?.();
        }
      }
      if (videoType === 'youtube' && (event.origin === 'https://www.youtube-nocookie.com' || event.origin === 'https://www.youtube.com')) {
        const data_0 = parseData();
        if (data_0?.info?.playerState === 1) {
          onPlayRef.current?.();
        }
      }
    };
    window.addEventListener('message', handleMessage);
    container.append(iframe);
    const handleLoad = () => {
      onReadyRef.current?.();

      // Subscribe to Vimeo play events via postMessage API
      if (videoType === 'vimeo' && iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({
          method: 'addEventListener',
          value: 'play'
        }), 'https://player.vimeo.com');
      }
      // Subscribe to YouTube state changes via IFrame API
      if (videoType === 'youtube' && iframe.contentWindow) {
        const ytOrigin = new URL(iframe.src).origin;
        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'listening'
        }), ytOrigin);
      }
    };
    iframe.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('message', handleMessage);
      iframe.removeEventListener('load', handleLoad);
      playerRef.current = null;
      iframe.remove();
    };
  }, [src, videoType, isBackground, title, enabled]);
  return {
    containerRef,
    playerRef
  };
};